import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { createId } from '@paralleldrive/cuid2'
import * as Sentry from '@sentry/nextjs'
import {
  IconArrowLeft,
  IconCamera,
  IconCameraRotate,
  IconChevronDown,
  IconLibraryPhoto,
} from '@tabler/icons-react'
import { RcFile } from 'antd/lib/upload'
import { get, set, remove } from 'local-storage'
import styled, { css, keyframes } from 'styled-components'

import { Button } from './Button'
import { Modal } from './Modal'
import { Popover, PopoverItem } from './Popover'
import { Text } from './Text'

import { useConfirm } from '@/context/confirmContext'
import { pluraliseWord } from '@/utils/index'

const LS_KEY = 'buildpass-deviceId'

const Shutter = keyframes`
  from {
    filter: brightness(0);
  }
  to {
    filter: brightness(1);
  }
`

const Video = styled.video<{ animate: boolean }>`
  width: 100%;
  height: 100%;
  background: black;

  ${({ animate }) =>
    animate &&
    css`
      animation: ${Shutter} 0.4s ease-out;
    `}
`

const ErrorWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  text-align: center;
`

const CaptureButton = styled.button`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colour.neutral.white};
  border-radius: 50%;
  width: 4rem;
  height: 4rem;
  border: none;
  outline: 4px solid ${({ theme }) => theme.colour.neutral.white};
  outline-offset: 4px;
  cursor: pointer;
`

const IconWrapper = styled.div`
  & button {
    color: ${({ theme }) => theme.colour.neutral.white};

    &:focus {
      color: ${({ theme }) => theme.colour.secondary.base};
    }
  }
`

const TopBar = styled(IconWrapper)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  right: 1rem;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
`

const Disclaimer = styled(Text)`
  position: absolute;
  top: 100%;
  left: 50%;
  width: 100%;
  transform: translateX(-50%);
  text-align: center;
  color: white;
  opacity: 0.8;
`

const GalleryWrapper = styled(IconWrapper)`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
`

const PreviewWrapper = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
`

const Grow = keyframes`
  from {
    transform: scale(0.3);
  }
  to {
    transform: scale(1);
  }

`

const PreviewImage = styled.img`
  width: 3rem;
  height: 3rem;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  animation: ${Grow} 0.2s ease-out forwards;
`

const PreviewCount = styled.div`
  position: absolute;
  bottom: -0.5rem;
  right: -0.5rem;
  background: ${({ theme }) => theme.colour.neutral.white};
  color: ${({ theme }) => theme.colour.secondary.base};
  border-radius: 50%;
  min-width: 1.5rem;
  min-height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`

enum State {
  Loading = 'loading',
  Ready = 'ready',
  Error = 'error',
}

export const hasCameraSupport = async () => {
  try {
    if (typeof global?.window?.navigator === 'undefined') {
      return false
    }

    const allowed = await navigator?.permissions?.query({
      name: 'camera' as unknown as PermissionName,
    })

    if (!allowed) return false
    if (allowed.state === 'denied') return false

    return true
  } catch (error) {
    Sentry.captureException(error)
    return false
  }
}

const getVideoStream = async (
  videoElement: HTMLVideoElement,
  deviceId?: string
): Promise<[MediaStream, string]> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: deviceId ? undefined : 'environment', deviceId },
      audio: false,
    })

    if (deviceId && !stream) {
      remove(LS_KEY)
      return getVideoStream(videoElement) // try again without the device Id incase it has changed
    }

    const newDeviceId = stream.getVideoTracks()?.[0]?.getSettings()?.deviceId

    videoElement.srcObject = stream
    videoElement.play()

    return [stream, newDeviceId]
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}

const takePicture = (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement
): Promise<RcFile> => {
  return new Promise((resolve, reject) => {
    const context = canvas.getContext('2d')

    if (!context) return reject()

    const width = video.videoWidth
    const height = video.videoHeight
    canvas.width = width
    canvas.height = height
    context.drawImage(video, 0, 0, width, height)

    // convert data to a file
    canvas.toBlob((blob) => {
      if (!blob) return reject()

      const file = new File([blob], `image-${new Date().getTime()}.png`, {
        type: 'image/png',
      }) as RcFile

      file.uid = createId()

      resolve(file)
    })
  })
}

type Props = {
  open: boolean
  onClose: () => void
  onLibraryPhotoClick: () => void
  addFiles: (files: RcFile[]) => void
}

export const CameraModal: React.FC<Props> = ({
  open,
  onClose,
  onLibraryPhotoClick,
  addFiles,
}) => {
  const videoElement = useRef<HTMLVideoElement>(null)
  const canvasElement = useRef<HTMLCanvasElement>(null)
  const videoStream = useRef<MediaStream | null>(null)

  const [devicePopoverOpen, setDevicePopoverOpen] = useState(false)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>()
  const [deviceId, setDeviceId] = useState<string>(get(LS_KEY))
  const [animate, setAnimate] = useState(false)
  const [state, setState] = useState(State.Loading)
  const [files, setFiles] = useState<RcFile[]>([])

  const { showConfirm } = useConfirm()

  const startCamera = useCallback(() => {
    if (!videoElement.current) return

    getVideoStream(videoElement.current, deviceId).then((response) => {
      const [stream, deviceId] = response || [null, null]
      if (stream) {
        videoStream.current = stream
        setDevice(deviceId)
        setState(State.Ready)
      } else {
        setState(State.Error)
      }
    })
  }, [deviceId])

  const setDevice = (id: string) => {
    setDevicePopoverOpen(false)
    setDeviceId(id)
    set(LS_KEY, id)
  }

  const flipCamera = () => {
    if (!deviceId) return

    const device = devices.find((device) => device.deviceId !== deviceId) // find first device with different id
    if (device?.deviceId) {
      setDevice(device.deviceId)
    }
  }

  const captureImage = async () => {
    if (!videoElement.current) {
      return
    }

    const file = await takePicture(videoElement.current, canvasElement.current)
    setFiles((files) => [...files, file])
    setAnimate(true)
  }

  const closeAndStopVideo = () => {
    if (videoStream.current) {
      videoStream.current.getTracks().forEach((track) => track.stop())
    }
    setDevicePopoverOpen(false)
    onClose()
  }

  const onDone = () => {
    addFiles(files)
    setFiles([])
    closeAndStopVideo()
  }

  const onGoToLibrary = async () => {
    const confirmed = await closeModal()

    if (confirmed) {
      onLibraryPhotoClick()
    }
  }

  const closeModal = async () => {
    if (!files.length) {
      closeAndStopVideo()
      return true
    }

    const confirmed = await showConfirm({
      title: 'Discard images?',
      message:
        'You have taken some photos. Are you sure you want to discard them?',
      confirmText: 'Discard',
      isDestructive: true,
    })

    if (confirmed) {
      setFiles([])
      closeAndStopVideo()
    }

    return confirmed
  }

  const recentFile = useMemo(() => {
    return files[files.length - 1]
  }, [files])

  useEffect(() => {
    const vid = videoElement.current

    if (open) {
      startCamera()

      // get all video devices
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        const validDevices = devices.filter((device) => {
          return device.kind === 'videoinput' && !!device.deviceId
        })
        setDevices(validDevices)
      })
    }

    return () => {
      if (vid) {
        vid.pause()
        vid.src = ''
      }

      if (videoStream.current) {
        videoStream.current.getVideoTracks().forEach((track) => track.stop())
      }
    }
  }, [startCamera, open])

  return (
    <Modal
      fullscreen
      noPadding
      open={open}
      onCancel={closeModal}
      closable={false}
    >
      {state === State.Error ? (
        <ErrorWrapper>
          <Text variant="label02" semantic="errorDark">
            Sorry, your device doesn&apos;t support multi-photo mode.
          </Text>
          <Button size="small" onClick={closeAndStopVideo}>
            Close
          </Button>
        </ErrorWrapper>
      ) : (
        <Video
          ref={videoElement}
          playsInline
          animate={animate}
          onAnimationEnd={() => setAnimate(false)}
          autoPlay={false}
        >
          <ErrorWrapper>
            <Text variant="label02" semantic="errorDark">
              Sorry, your device doesn&apos;t support multi-photo mode.
            </Text>
            <Button size="small" onClick={closeAndStopVideo}>
              Close
            </Button>
          </ErrorWrapper>
        </Video>
      )}

      <TopBar>
        <Disclaimer variant="caption01">
          Photo quality is reduced for multiple capture
        </Disclaimer>
        <Button
          variant="secondary-text"
          icon={<IconArrowLeft size={24} />}
          onClick={closeModal}
        />

        {devices?.length === 2 && (
          <Button
            variant="secondary-text"
            icon={<IconCameraRotate size={24} />}
            onClick={flipCamera}
          />
        )}

        {devices?.length > 2 && (
          <Popover
            open={devicePopoverOpen}
            onOpenChange={setDevicePopoverOpen}
            placement="bottom"
            content={devices.map((device) => (
              <PopoverItem
                key={device.deviceId}
                onClick={() => setDevice(device.deviceId)}
                active={deviceId === device.deviceId}
              >
                {device.label}
              </PopoverItem>
            ))}
          >
            <Button variant="secondary-text" icon={<IconCamera size={24} />}>
              <IconChevronDown size={24} />
            </Button>
          </Popover>
        )}

        <Button
          variant="secondary-text"
          onClick={onDone}
          disabled={!files.length}
        >
          Use {pluraliseWord(files.length, 'photo')}
        </Button>
      </TopBar>

      <GalleryWrapper>
        <Button
          variant="secondary-text"
          icon={<IconLibraryPhoto size={24} />}
          onClick={onGoToLibrary}
        />
      </GalleryWrapper>

      <CaptureButton onClick={captureImage} />

      <canvas ref={canvasElement} style={{ display: 'none' }} />

      <PreviewWrapper>
        {recentFile && (
          <>
            <PreviewImage
              src={URL.createObjectURL(recentFile)}
              key={recentFile.uid} // trigger animation each time
            />
            <PreviewCount>{files.length}</PreviewCount>
          </>
        )}
      </PreviewWrapper>
    </Modal>
  )
}
